import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { ChevronLeft, ChevronRight, Calendar, MapPin, Clock, Plus, X, Check } from 'lucide-react';
import { format, startOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

const APT_COLORS = {
  'seance': { bg: '#E0F2F0', text: '#2E665E', dot: '#4A9B8F' },
  'medical': { bg: '#DBEAFE', text: '#1E3A8A', dot: '#6B9FD4' },
  'activite': { bg: '#F3E8FF', text: '#6B21A8', dot: '#A78BCA' },
  'autre': { bg: '#F5F5F4', text: '#57534E', dot: '#94A3B8' },
};

const getColorForType = (type) => APT_COLORS[type] || APT_COLORS['autre'];

const formatTime = (dt) => {
  if (!dt) return '';
  try {
    const clean = dt.replace(' ', 'T').replace(/Z$/, '').replace(/\+.*$/, '');
    const [, time] = clean.split('T');
    const [h, m] = time.split(':');
    return `${h}h${m}`;
  } catch { return ''; }
};

export default function PatientPlanning() {
  const { user } = useAuth();
  const [currentWeekStart, setCurrentWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '', appointment_type: 'seance', date: '', start_time: '', end_time: '', location: '', notes: ''
  });

  useEffect(() => { if (user) loadAppointments(); }, [user, currentWeekStart]);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const weekEnd = addWeeks(currentWeekStart, 1);
      const { data } = await supabase
        .from('appointments')
        .select('*')
        .eq('patient_id', user.id)
        .gte('start_datetime', currentWeekStart.toISOString())
        .lt('start_datetime', weekEnd.toISOString())
        .order('start_datetime');
      setAppointments(data || []);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.date) return;
    setSaving(true);
    try {
      const start = formData.start_time ? `${formData.date}T${formData.start_time}:00` : `${formData.date}T00:00:00`;
      const end = formData.end_time ? `${formData.date}T${formData.end_time}:00` : `${formData.date}T01:00:00`;
      await supabase.from('appointments').insert({
        patient_id: user.id,
        title: formData.title,
        appointment_type: formData.appointment_type,
        start_datetime: start,
        end_datetime: end,
        location: formData.location,
        notes: formData.notes,
        status: 'confirmed',
      });
      setShowAdd(false);
      setFormData({ title: '', appointment_type: 'seance', date: '', start_time: '', end_time: '', location: '', notes: '' });
      loadAppointments();
    } finally {
      setSaving(false);
    }
  };

  const weekDays = eachDayOfInterval({ start: currentWeekStart, end: addWeeks(currentWeekStart, 1) }).slice(0, 7);
  const dayApts = appointments.filter(a => isSameDay(new Date(a.start_datetime), selectedDay));

  return (
    <div className="px-5 pt-5 pb-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="page-title">Planning</h1>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full font-heading font-semibold text-sm"
          style={{ backgroundColor: '#4A9B8F', color: 'white' }}>
          <Plus size={15} /> Ajouter
        </button>
      </div>

      {/* Navigation semaine */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCurrentWeekStart(w => subWeeks(w, 1))} className="p-2 rounded-xl bg-stone-100">
          <ChevronLeft size={18} className="text-slate-500" />
        </button>
        <span className="font-heading font-semibold text-sm text-slate-700 capitalize">
          {format(currentWeekStart, "d MMM", { locale: fr })} – {format(addWeeks(currentWeekStart, 1), "d MMM yyyy", { locale: fr })}
        </span>
        <button onClick={() => setCurrentWeekStart(w => addWeeks(w, 1))} className="p-2 rounded-xl bg-stone-100">
          <ChevronRight size={18} className="text-slate-500" />
        </button>
      </div>

      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 gap-1 mb-5">
        {weekDays.map(day => {
          const isSelected = isSameDay(day, selectedDay);
          const hasApts = appointments.some(a => isSameDay(new Date(a.start_datetime), day));
          return (
            <button key={day.toISOString()} onClick={() => setSelectedDay(day)}
              className={`flex flex-col items-center py-2 rounded-2xl transition-all ${isSelected ? 'bg-sage-500 text-white' : 'bg-white text-slate-600'}`}>
              <span className="text-[10px] font-heading font-semibold capitalize">
                {format(day, 'eee', { locale: fr }).slice(0, 2)}
              </span>
              <span className={`text-sm font-heading font-bold ${isSelected ? 'text-white' : 'text-slate-700'}`}>
                {format(day, 'd')}
              </span>
              {hasApts && <div className={`w-1.5 h-1.5 rounded-full mt-0.5 ${isSelected ? 'bg-white/60' : 'bg-sage-400'}`} />}
            </button>
          );
        })}
      </div>

      {/* RDV du jour sélectionné */}
      <p className="section-label mb-3 capitalize">{format(selectedDay, "EEEE d MMMM", { locale: fr })}</p>
      {loading ? (
        <div className="space-y-2">{[1,2].map(i => <div key={i} className="h-20 bg-stone-100 rounded-3xl animate-pulse" />)}</div>
      ) : dayApts.length === 0 ? (
        <div className="passerelle-card text-center py-10">
          <Calendar size={28} className="text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400 font-body">Aucun rendez-vous ce jour</p>
        </div>
      ) : (
        <div className="space-y-3">
          {dayApts.map(apt => {
            const colors = getColorForType(apt.appointment_type);
            return (
              <div key={apt.id} className="passerelle-card flex gap-4">
                <div className="w-1 rounded-full flex-shrink-0" style={{ backgroundColor: colors.dot }} />
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-semibold text-sm text-slate-800">{apt.title}</p>
                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                    <span className="flex items-center gap-1 text-xs text-slate-400 font-body">
                      <Clock size={12} /> {formatTime(apt.start_datetime)} – {formatTime(apt.end_datetime)}
                    </span>
                    {apt.location && (
                      <span className="flex items-center gap-1 text-xs text-slate-400 font-body">
                        <MapPin size={12} /> {apt.location}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[11px] font-heading font-semibold px-2 py-1 rounded-full flex-shrink-0 self-start"
                  style={{ backgroundColor: colors.bg, color: colors.text }}>
                  {apt.appointment_type}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal ajout RDV */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center">
          <div className="w-full max-w-[480px] bg-white rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg text-slate-800">Nouveau rendez-vous</h3>
              <button onClick={() => setShowAdd(false)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center">
                <X size={16} className="text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-slate-500 font-heading font-semibold mb-1 block">Titre *</label>
                <input type="text" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})}
                  placeholder="Ex: Séance orthophonie" className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700" />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-heading font-semibold mb-1 block">Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {[{value:'seance',label:'Séance'},{value:'medical',label:'Médical'},{value:'activite',label:'Activité'},{value:'autre',label:'Autre'}].map(t => (
                    <button key={t.value} onClick={() => setFormData({...formData, appointment_type: t.value})}
                      className={`py-2 rounded-xl text-sm font-heading font-semibold border-2 transition-all ${formData.appointment_type === t.value ? 'border-sage-400 bg-sage-50 text-sage-700' : 'border-stone-200 text-slate-500'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-heading font-semibold mb-1 block">Date *</label>
                <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})}
                  className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500 font-heading font-semibold mb-1 block">Début</label>
                  <input type="time" value={formData.start_time} onChange={e => setFormData({...formData, start_time: e.target.value})}
                    className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-heading font-semibold mb-1 block">Fin</label>
                  <input type="time" value={formData.end_time} onChange={e => setFormData({...formData, end_time: e.target.value})}
                    className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-heading font-semibold mb-1 block">Lieu</label>
                <input type="text" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})}
                  placeholder="Ex: Cabinet Dr. Martin" className="w-full h-11 px-4 bg-stone-50 border border-stone-200 rounded-2xl text-sm font-body text-slate-700" />
              </div>
              <button onClick={handleSubmit} disabled={!formData.title || !formData.date || saving}
                className="w-full h-12 rounded-2xl font-heading font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ backgroundColor: '#4A9B8F' }}>
                {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <><Check size={16} /> Ajouter</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
